import { FC } from "react";
import { Platform } from "react-native";
import * as ProgressPrimitive from "@rn-primitives/progress";
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withSpring,
} from "react-native-reanimated";

import { usePlatformOS } from "@/hooks/usePlatformOS";
import { cn } from "@/utils/tailwind";

import { VnBox } from "./VnBox";

type IndicatorPropsType = {
  value: number | undefined | null;
  className?: string;
};

const WebIndicator: FC<IndicatorPropsType> = ({ value, className }) => {
  if (Platform.OS !== "web") {
    return null;
  }

  return (
    <VnBox
      className={cn(
        "bg-sidebar h-full w-full flex-1 transition-all",
        className,
      )}
      style={{ transform: `translateX(-${100 - (value ?? 0)}%)` }}>
      <ProgressPrimitive.Indicator className={cn("h-full w-full", className)} />
    </VnBox>
  );
};

const NativeIndicator: FC<IndicatorPropsType> = ({ value, className }) => {
  const progress = useDerivedValue(() => value ?? 0);

  const indicator = useAnimatedStyle(() => {
    return {
      width: withSpring(
        `${interpolate(progress.value, [0, 100], [1, 100], Extrapolation.CLAMP)}%`,
        { overshootClamping: true },
      ),
    };
  }, [value]);

  if (Platform.OS === "web") {
    return null;
  }

  return (
    <ProgressPrimitive.Indicator asChild>
      <Animated.View
        style={indicator}
        className={cn("bg-foreground h-full", className)}
      />
    </ProgressPrimitive.Indicator>
  );
};

type VnProgressPropsType = ProgressPrimitive.RootProps & {
  indicatorClassName?: string;
};

export const VnProgress: FC<VnProgressPropsType> = ({
  className,
  value,
  indicatorClassName,
  ...props
}) => {
  const { isWeb, isNative } = usePlatformOS();

  return (
    <ProgressPrimitive.Root
      className={cn(
        "bg-sidebar/20 relative h-2 w-full overflow-hidden rounded-full",
        className,
      )}
      {...props}>
      {isNative ? (
        <NativeIndicator value={value} className={indicatorClassName} />
      ) : isWeb ? (
        <WebIndicator value={value} className={indicatorClassName} />
      ) : null}
    </ProgressPrimitive.Root>
  );
};
